/** API_SPEC.md §6 — applications and the manual-weekly baseline. */
import { api } from './client';
import type { Application, ApplicationCreateRequest, ApplicationStatus, ApplicationStatusChange, ManualWeekly, Paginated } from './types';

export const applicationsApi = {
  list: (q: { status?: ApplicationStatus; q?: string; page?: number; per_page?: number } = {}) =>
    api<Paginated<Application>>('/v1/applications', { query: { status: q.status, q: q.q, page: q.page, per_page: q.per_page } }),
  /**
   * Unique per (user, job): a repeat answers 200 with the existing record.
   * `keepalive`: the job page shows the application as tracked before this
   * answers, so the request has to outlive a tab closed straight after.
   */
  create: (body: ApplicationCreateRequest) => api<Application>('/v1/applications', { method: 'POST', body, keepalive: true }),
  setStatus: (id: string, status: ApplicationStatus) => api<ApplicationStatusChange>(`/v1/applications/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status } }),
  /** Career Compass (ticket 12): undo the last status change, once, within 15 s; else 409. 404 while the server flag is off. */
  undoStatus: (id: string) => api<Application>(`/v1/applications/${encodeURIComponent(id)}/undo-status`, { method: 'POST' }),
  /** "Not yet" (ticket 12): out of "Any replies this week?" for 7 days. 404 while the server flag is off. */
  snoozePrompt: (id: string) => api<void>(`/v1/applications/${encodeURIComponent(id)}/snooze-prompt`, { method: 'POST' }),
  remove: (id: string) => api<void>(`/v1/applications/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  manualWeekly: () => api<ManualWeekly>('/v1/stats/manual-weekly'),
};

/**
 * A user's applications are a small, personal set (tens, not thousands), and
 * the Tracking screen filters, searches and counts them instantly. Loading them
 * all (paged) keeps that behaviour exact without a counts endpoint the
 * contract doesn't have; `q`/`status` on the server stay available for the day
 * the set outgrows this.
 */
export async function listAllApplications(): Promise<Application[]> {
  const out: Application[] = [];
  const perPage = 100;
  for (let page = 1; page <= 10; page++) {
    const res = await applicationsApi.list({ page, per_page: perPage });
    out.push(...res.items);
    if (out.length >= res.total || res.items.length < perPage) break;
  }
  return out;
}
